import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  CircularProgress,
  Typography,
  Autocomplete,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { FuelCardActionModalProps, FuelCardType } from "./types";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { commonTextFieldProps, smallButtonProp } from "../../../utils/styles";
import { fuelCardApiService } from "./apiService";
import { Loading } from "../../../components/Loading";

const validationSchema = yup.object().shape({
  accountNumber: yup.string().required("Account Number is required"),
  vehicleNumber: yup.string().required("Vehicle Number is required"),
  provider: yup.string().required("Provider is required"),
  location: yup.string().nullable(),
});

const FuelCardActionModal = ({
  open,
  onClose,
  fuelCard,
  isEdit = false,
  onSuccess,
}: FuelCardActionModalProps) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fuelCardOptions = useMemo(() => {
    return ["BPCL", "HPCL", "IOCL", "HDFC Petro Card"];
  }, []);

  const initialValues: FuelCardType = {
    id: null,
    accountNumber: "",
    vehicleNumber: "",
    provider: "",
    location: null,
    companyId,
  };

  const handleSubmit = async (values: FuelCardType) => {
    setIsSubmitting(true);
    if (isEdit) {
      const updatedFuelCard = await fuelCardApiService.updateFuelCard(values);
      if (updatedFuelCard) {
        onClose();
        onSuccess?.();
      }
      setIsSubmitting(false);
    } else {
      const fuelCard = await fuelCardApiService.createFuelCard(values);
      if (fuelCard) {
        onClose();
        onSuccess?.();
      }
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", py: 0.5 }}>
        {isEdit ? "Edit Fuel Card" : "Add New Fuel Card"}
      </DialogTitle>
      <DialogContent>
        {isSubmitting ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 2,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body1">Submitting...</Typography>
          </Box>
        ) : (
          <Formik
            initialValues={isEdit && fuelCard ? fuelCard : initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
              <Form>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      name="accountNumber"
                      value={values.accountNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.accountNumber && touched.accountNumber}
                      helperText={errors.accountNumber}
                      {...commonTextFieldProps}
                      inputProps={{
                        maxLength: 20,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Vehicle Number"
                      name="vehicleNumber"
                      value={values.vehicleNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.vehicleNumber && touched.vehicleNumber}
                      helperText={errors.vehicleNumber}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      options={fuelCardOptions}
                      value={values.provider}
                      onChange={(event, value) => {
                        setFieldValue("provider", value || "");
                      }}
                      onInputChange={(event, newInputValue) => {
                        setFieldValue("provider", newInputValue);
                      }}
                      onBlur={handleBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Provider"
                          name="provider"
                          error={!!errors.provider && touched.provider}
                          helperText={errors.provider}
                          {...commonTextFieldProps}
                        />
                      )}
                      freeSolo
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {option}
                        </Box>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Location"
                      name="location"
                      value={values.location}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.location && touched.location}
                      helperText={errors.location}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 2,
                    gap: 2,
                  }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    {...smallButtonProp}
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    {...smallButtonProp}
                  >
                    {isEdit ? "Update" : "Add"}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FuelCardActionModal;
